import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Sparkles, Package, ImageIcon, ArrowRight, Mail, FileDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { productsData, subCategoryNames } from '../data/productsData';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { DownloadCatalogButton } from './DownloadCatalogButton';

const SUBCAT_ORDER = ['kitchen', 'laundry', 'home', 'personal-care'];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SUBCAT_ICONS = {
  kitchen: '🍽️',
  laundry: '👕',
  home: '🏠',
  'personal-care': '💧',
};

const SUBCAT_GRADIENTS = {
  kitchen: 'from-orange-500 to-amber-600',
  laundry: 'from-sky-500 to-blue-600',
  home: 'from-emerald-500 to-teal-600',
  'personal-care': 'from-pink-500 to-rose-600',
};

export const CatalogSection = () => {
  const { t, language, direction } = useLanguage();
  const [selectedSubCat, setSelectedSubCat] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const getName = (p) => (language === 'ar' ? p.nameAr : language === 'en' ? p.nameEn : p.nameFr);
  const getDesc = (p) => (language === 'ar' ? p.descriptionAr : language === 'en' ? p.descriptionEn : p.descriptionFr);
  const getFeatures = (p) => (language === 'ar' ? p.featuresAr : language === 'en' ? p.featuresEn : p.featuresFr);

  const stats = useMemo(() => {
    const byCat = {};
    productsData.forEach((p) => {
      byCat[p.subCategory] = (byCat[p.subCategory] || 0) + 1;
    });
    return byCat;
  }, []);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return productsData.filter((p) => {
      const matchSub = selectedSubCat === 'all' || p.subCategory === selectedSubCat;
      const matchSearch =
        !q ||
        p.nameFr.toLowerCase().includes(q) ||
        p.nameEn.toLowerCase().includes(q) ||
        p.nameAr.includes(searchQuery) ||
        getFeatures(p).join(' ').toLowerCase().includes(q);
      return matchSub && matchSearch;
    });
  }, [selectedSubCat, searchQuery, language]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((p) => {
      if (!g[p.subCategory]) g[p.subCategory] = [];
      g[p.subCategory].push(p);
    });
    return g;
  }, [filtered]);

  const scrollToContact = () => {
    window.location.href = '/#contact';
  };

  return (
    <section id="catalogue" className="py-16 bg-gradient-to-b from-blue-50/40 to-white" dir={direction}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">

        {/* Hero header */}
        <div className="text-center max-w-4xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Sparkles className="w-4 h-4" />
            {t('catalog.title')}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
            {language === 'ar' ? 'مجموعة منتجاتنا الكاملة' : language === 'en' ? 'Our Complete Product Range' : 'Notre Gamme Complète'}
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {language === 'ar'
              ? 'اكتشف 56 منتجاً مطوراً في مختبرنا، موزعة على 4 فئات لتلبية جميع احتياجاتك المنزلية والمهنية.'
              : language === 'en'
              ? 'Discover 56 lab-developed products across 4 categories, designed to meet every household and professional need.'
              : 'Découvrez 56 produits développés dans nos laboratoires, répartis sur 4 catégories pour répondre à tous vos besoins domestiques et professionnels.'}
          </p>

          {/* Stats Strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10 max-w-3xl mx-auto">
            {SUBCAT_ORDER.map((sub) => (
              <div
                key={sub}
                className={`bg-gradient-to-br ${SUBCAT_GRADIENTS[sub]} rounded-2xl p-5 text-white shadow-lg`}
                data-testid={`stat-${sub}`}
              >
                <div className="text-3xl mb-2">{SUBCAT_ICONS[sub]}</div>
                <div className="text-3xl font-bold">{stats[sub] || 0}</div>
                <div className="text-sm opacity-90 mt-1">{subCategoryNames[sub][language]}</div>
              </div>
            ))}
          </div>

          {/* PDF Download CTA */}
          <div className="mt-10 flex justify-center">
            <DownloadCatalogButton language={language} testid="download-pdf-btn" />
          </div>
        </div>

        {/* Filters bar */}
        <div className="sticky top-20 z-30 bg-white/80 backdrop-blur-md rounded-2xl p-4 mb-10 shadow-md border border-gray-100">
          <div className="flex flex-col lg:flex-row gap-4 items-stretch">
            {/* Search */}
            <div className="relative flex-1">
              <Search className={`absolute ${direction === 'rtl' ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400`} />
              <Input
                type="text"
                data-testid="catalog-search-input"
                placeholder={
                  language === 'ar'
                    ? 'ابحث عن منتج أو خاصية...'
                    : language === 'en'
                    ? 'Search a product or feature...'
                    : 'Rechercher un produit ou une caractéristique...'
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full ${direction === 'rtl' ? 'pr-12' : 'pl-12'} py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base`}
              />
            </div>

            {/* Subcategory pills */}
            <div className="flex gap-2 flex-wrap justify-center lg:justify-start">
              <button
                onClick={() => setSelectedSubCat('all')}
                data-testid="filter-all"
                className={`px-5 py-2.5 rounded-xl font-semibold text-sm transition-all whitespace-nowrap ${
                  selectedSubCat === 'all'
                    ? 'bg-gradient-to-r from-blue-700 to-blue-800 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {language === 'ar' ? 'الكل' : language === 'en' ? 'All' : 'Tous'} ({productsData.length})
              </button>
              {SUBCAT_ORDER.map((sub) => (
                <button
                  key={sub}
                  onClick={() => setSelectedSubCat(sub)}
                  data-testid={`filter-${sub}`}
                  className={`px-5 py-2.5 rounded-xl font-semibold text-sm transition-all whitespace-nowrap inline-flex items-center gap-2 ${
                    selectedSubCat === sub
                      ? `bg-gradient-to-r ${SUBCAT_GRADIENTS[sub]} text-white shadow-md`
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>{SUBCAT_ICONS[sub]}</span>
                  {subCategoryNames[sub][language]} ({stats[sub] || 0})
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Products by category */}
        {SUBCAT_ORDER.filter((sub) => (selectedSubCat === 'all' || selectedSubCat === sub) && grouped[sub]?.length).map((sub) => (
          <div key={sub} className="mb-16" id={`section-${sub}`}>
            {/* Category title */}
            <div className="flex items-center gap-4 mb-8">
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${SUBCAT_GRADIENTS[sub]} flex items-center justify-center text-3xl shadow-lg`}>
                {SUBCAT_ICONS[sub]}
              </div>
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                  {subCategoryNames[sub][language]}
                </h2>
                <p className="text-gray-500 text-sm mt-1">
                  {grouped[sub].length}{' '}
                  {language === 'ar' ? 'منتج' : language === 'en' ? 'products' : 'produits'}
                </p>
              </div>
            </div>

            {/* Products grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {grouped[sub].map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  name={getName(product)}
                  desc={getDesc(product)}
                  features={getFeatures(product)}
                  gradient={SUBCAT_GRADIENTS[sub]}
                  direction={direction}
                  language={language}
                />
              ))}
            </div>
          </div>
        ))}

        {/* No results */}
        {filtered.length === 0 && (
          <div className="text-center py-20">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-xl text-gray-500">
              {language === 'ar'
                ? 'لا توجد منتجات تطابق بحثك'
                : language === 'en'
                ? 'No products match your search'
                : 'Aucun produit ne correspond à votre recherche'}
            </p>
          </div>
        )}

        {/* CTA Footer */}
        <div className="mt-16 bg-gradient-to-r from-blue-700 via-blue-800 to-blue-900 rounded-3xl p-10 text-white shadow-2xl">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-3xl sm:text-4xl font-bold mb-4">
              {language === 'ar'
                ? 'بحاجة إلى عرض أسعار أو شراكة دولية ؟'
                : language === 'en'
                ? 'Need a quote or international partnership ?'
                : 'Besoin d\'un devis ou d\'un partenariat international ?'}
            </h3>
            <p className="text-blue-100 text-lg mb-8 leading-relaxed">
              {language === 'ar'
                ? 'فريقنا التجاري في خدمتك للعروض الكبيرة والصادرات والشراكات الاستراتيجية.'
                : language === 'en'
                ? 'Our commercial team is at your service for large orders, exports and strategic partnerships.'
                : 'Notre équipe commerciale est à votre service pour les grandes commandes, l\'export et les partenariats stratégiques.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={scrollToContact}
                data-testid="catalog-cta-contact"
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 rounded-full text-lg font-semibold shadow-xl hover:scale-105 transition-transform"
              >
                <Mail className="w-5 h-5 mr-2" />
                {language === 'ar' ? 'اتصل بنا' : language === 'en' ? 'Contact us' : 'Nous contacter'}
              </Button>
              <DownloadCatalogButton
                language={language}
                variant="ghost"
                testid="catalog-cta-pdf"
                className="bg-white/10 backdrop-blur-sm border-2 border-white/40 text-white hover:bg-white hover:text-blue-900 px-8 py-6 rounded-full text-lg font-semibold shadow-xl hover:scale-105 transition-transform"
              />
              <a
                href="https://olion.odoo.com/shop"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="catalog-cta-shop"
                className="inline-flex items-center justify-center bg-white text-blue-800 hover:bg-blue-50 px-8 py-6 rounded-full text-lg font-semibold shadow-xl hover:scale-105 transition-transform"
              >
                {language === 'ar' ? 'المتجر الإلكتروني' : language === 'en' ? 'Online Shop' : 'Boutique en ligne'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============ Product Card Component ============
const ProductCard = ({ product, name, desc, features, gradient, direction, language }) => {
  const channelLabel = product.category === 'industrial'
    ? (language === 'ar' ? 'احترافي / صناعي' : language === 'en' ? 'Professional / Industrial' : 'Pro / Industriel')
    : (language === 'ar' ? 'البيع بالتجزئة' : language === 'en' ? 'Retail' : 'Retail');

  return (
    <Link
      to={`/catalogue/${product.id}`}
      data-testid={`product-card-${product.id}`}
      className="group relative bg-white rounded-2xl shadow-sm hover:shadow-2xl border border-gray-100 overflow-hidden transition-all duration-500 hover:-translate-y-1 flex flex-col cursor-pointer"
    >
      {/* Image area — pure white to blend with product photo backgrounds */}
      <div className="relative h-64 bg-white overflow-hidden flex items-center justify-center p-6">
        {product.hasImage ? (
          <img
            src={product.image}
            alt={name}
            loading="lazy"
            style={{ mixBlendMode: 'multiply' }}
            className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-700"
          />
        ) : (
          <div className="flex flex-col items-center justify-center text-gray-300 gap-3">
            <ImageIcon className="w-16 h-16" strokeWidth={1} />
            <span className="text-xs uppercase tracking-wider font-semibold">
              {language === 'ar' ? 'صورة قريباً' : language === 'en' ? 'Photo coming soon' : 'Photo bientôt'}
            </span>
          </div>
        )}

        {/* Size badge */}
        <div className={`absolute top-4 ${direction === 'rtl' ? 'left-4' : 'right-4'} bg-gradient-to-r ${gradient} text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-md`}>
          {product.size}
        </div>

        {/* Channel badge */}
        <div className={`absolute top-4 ${direction === 'rtl' ? 'right-4' : 'left-4'} bg-gray-900/85 backdrop-blur-sm text-white px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider shadow-sm`}>
          {channelLabel}
        </div>
      </div>

      {/* Info — light grey background */}
      <div className="p-5 flex-1 flex flex-col bg-gray-50 border-t border-gray-100">
        <h3 className="text-base font-bold text-gray-900 mb-2 leading-tight min-h-[3rem]" title={name}>
          {name}
        </h3>

        <p className="text-xs text-gray-500 mb-4 line-clamp-3 leading-relaxed">{desc}</p>

        {/* Features bullets */}
        <ul className="space-y-1.5 mb-4 flex-1">
          {features.slice(0, 3).map((f, idx) => (
            <li key={idx} className="flex items-start gap-2 text-xs text-gray-700">
              <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${gradient} mt-1.5 flex-shrink-0`} />
              <span className="leading-relaxed">{f}</span>
            </li>
          ))}
        </ul>

        {/* Bottom strip */}
        <div className="mt-auto pt-3 border-t border-gray-100">
          <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold" style={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}>
            {language === 'ar' ? 'مرجع' : language === 'en' ? 'Reference' : 'Référence'} · {product.id.toUpperCase()}
          </div>
        </div>
      </div>
    </Link>
  );
};
